import { Link, useParams } from "react-router-dom";
import PageLayout from "@/components/feature/PageLayout";
import { getNewsById, news } from "@/mocks/news";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function NewsDetailPage() {
  const { id } = useParams<{ id: string }>();
  const item = id ? getNewsById(id) : undefined;

  usePageMeta(
    item ? `${item.title} - 80s电影` : "资讯未找到 - 80s电影",
    item ? item.summary : undefined,
    item ? `${item.category},80s电影,影视资讯` : undefined
  );

  if (!item) {
    return (
      <PageLayout>
        <div className="mx-auto flex min-h-[50vh] w-full max-w-2xl flex-col items-center justify-center px-4 text-center">
          <i className="ri-newspaper-line mb-4 flex h-16 w-16 items-center justify-center text-4xl text-foreground-300"></i>
          <h1 className="font-heading text-xl font-bold text-foreground-950">
            没有找到这篇资讯
          </h1>
          <Link
            to="/news"
            className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm font-semibold text-background-50"
          >
            返回资讯列表
          </Link>
        </div>
      </PageLayout>
    );
  }

  const related = news.filter((n) => n.id !== item.id).slice(0, 3);

  return (
    <PageLayout>
      <article className="mx-auto w-full max-w-3xl px-4 py-10 md:px-6">
        <nav className="mb-4 flex items-center gap-1.5 font-label text-xs text-foreground-500">
          <Link to="/" className="hover:text-accent-600">
            首页
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/news" className="hover:text-accent-600">
            影视资讯
          </Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-800 line-clamp-1">{item.title}</span>
        </nav>

        <span className="rounded-sm bg-secondary-100 px-2 py-0.5 font-label text-xs text-secondary-900">
          {item.category}
        </span>
        <h1 className="mt-3 font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-3xl">
          {item.title}
        </h1>
        <p className="mt-2 font-label text-sm text-foreground-500">
          发布日期：{item.date}
        </p>

        <div className="mt-6 overflow-hidden rounded-lg bg-primary-950">
          <img
            src={item.cover}
            alt={item.title}
            title={item.title}
            className="aspect-video w-full object-top"
          />
        </div>

        <div className="mt-8 space-y-5">
          {item.content.map((block, idx) => {
            if (block.type === "p") {
              return (
                <p
                  key={idx}
                  className="font-label text-sm leading-relaxed text-foreground-700 md:text-base"
                >
                  {block.text}
                </p>
              );
            }
            if (block.type === "quote") {
              return (
                <blockquote
                  key={idx}
                  className="border-l-4 border-accent-400 bg-accent-50 px-4 py-3 font-heading text-base italic text-foreground-800"
                >
                  {block.text}
                </blockquote>
              );
            }
            return (
              <figure key={idx} className="overflow-hidden rounded-lg">
                <img
                  src={block.src}
                  alt={block.caption || item.title}
                  title={block.caption || item.title}
                  className="w-full object-top"
                />
                {block.caption && (
                  <figcaption className="mt-2 font-label text-xs text-foreground-500">
                    {block.caption}
                  </figcaption>
                )}
              </figure>
            );
          })}
        </div>

        {related.length > 0 && (
          <section className="mt-12 border-t border-background-200 pt-8">
            <h2 className="font-heading text-lg font-bold text-foreground-950">
              相关文章推荐
            </h2>
            <ul className="mt-4 space-y-3">
              {related.map((n) => (
                <li key={n.id}>
                  <Link
                    to={`/news/${n.id}`}
                    className="flex items-center gap-2 font-label text-sm text-foreground-700 hover:text-accent-600"
                  >
                    <i className="ri-arrow-right-s-line text-accent-500"></i>
                    <span className="line-clamp-1">{n.title}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className="mt-10">
          <Link
            to="/news"
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 font-label text-sm font-semibold text-foreground-700 transition-colors hover:border-accent-400 hover:text-accent-600"
          >
            <i className="ri-arrow-left-line"></i>
            返回资讯列表
          </Link>
        </div>
      </article>
    </PageLayout>
  );
}